/** @format */

import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Router, Redirect, Location, navigate, useLocation } from "@reach/router";
import qs from "qs";
import loadable from "@loadable/component";

import { REDIRECT_AFTER_LOGIN_KEY } from "../constants/redirectAfterLogin";
import { useLanguage } from "../i18n";

import ErrorBoundary from "./../components/helpers/ErrorBoundary";
import { isAllowedToRead } from "../helper/permissions";

import MainScreen from "./index";
import SharingPointsScreenMobile from "./SharingPointsScreenMobile";
import SharingPointDetailScreen from "./SharingPointDetailScreen";
import ReleaseNotes from "./../components/ReleaseNotes";
import SwapDetailView from "./../components/SwapDetailView";

import { isAllowedToWrite } from "./../helper/permissions";

import NewLayout from "../components/NewLayout";
import { RestApiMessenger } from "../components/RestApiMessenger/RestApiMessenger";
import { UserGroupDetailScreen } from "./UserGroupDetailScreen";
import { Grid, GridColumn, Header, Icon } from "semantic-ui-react";
import SwobbeePublicLocationMap from "../components/SwobbeePublicLocationMap";

const SwapStatisticScreen = loadable(() =>
  import("./../components/SwapStatistics")
);

const SystemSummaryScreen = loadable(() => import("./SystemSummaryScreen"));



const LocationsScreen = loadable(() => import("./locations"));
const LocationsCreateScreen = loadable(() => import("./locations/create"));
const ShowLocationsScreen = loadable(() => import("./locations/show"));

const BatteriesScreen = loadable(() => import("./batteries"));

const SharingPointsIndexScreen = loadable(() =>
  import("./sharingPoints/index")
);
const SharingPointsScreen = loadable(() => import("./sharingPoints"));
const ShowSharingPointScreen = loadable(() => import("./sharingPoints/show"));

const UserManagementScreen = loadable(() => import("./UserManagementScreen"));

const ProfileScreen = loadable(() => import("./Profile"));

const AuthenticatedApp = () => {
  const session = useSelector((state) => state.session);
  const acks = useSelector((state) => state.acks.acks);
  const redirectHandled = useRef(false);
  const { language: lang, setLanguage: setLang } = useLanguage();

  const collapsed = useSelector((state) => {
    return state.appSettings.sidebarIsCollapsed;
  });
  const { restApiResponse } = useSelector((state) => state.restApi);

  useEffect(() => {
    if (!session.loggedIn || redirectHandled.current) return;
    try {
      let redirect = sessionStorage.getItem(REDIRECT_AFTER_LOGIN_KEY);
      if (redirect && typeof redirect === "string" && redirect.startsWith("/") && !redirect.startsWith("//")) {
        redirectHandled.current = true;
        sessionStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
        const hashIdx = redirect.indexOf("#");
        const hash = hashIdx >= 0 ? redirect.slice(hashIdx) : "";
        const withoutHash = hashIdx >= 0 ? redirect.slice(0, hashIdx) : redirect;
        const qIdx = withoutHash.indexOf("?");
        const pathname = qIdx >= 0 ? withoutHash.slice(0, qIdx) : withoutHash;
        const search = qIdx >= 0 ? withoutHash.slice(qIdx + 1) : "";
        const params = new URLSearchParams(search);
        params.delete("sorted_by");
        params.delete("order");
        const newSearch = params.toString() ? "?" + params.toString() : "";
        navigate(pathname + newSearch + hash);
      }
    } catch (_) {}
  }, [session.loggedIn]);

  return (
    <ErrorBoundary>
      <Location>
        {() => (
          <NewLayout
            lang={lang}
            setLang={setLang}
            collapsed={Boolean(collapsed)}
            session={session}
          >
            <Router>
              {!isAllowedToRead("ErrorHistory") &&
                !isAllowedToRead("SharingPoint") && (
                <SharingPointsScreen path="/">
                  <SharingPointsIndexScreen path="/" />
                  <ShowSharingPointScreen path="/:sharingPointId"></ShowSharingPointScreen>
                </SharingPointsScreen>
              )}

              {isAllowedToRead("ErrorHistory") && (
                <SharingPointsScreen path="/sharingpoints">
                  <SharingPointsIndexScreen path="/" className="xx" />
                  <ShowSharingPointScreen path="/:sharingPointId"></ShowSharingPointScreen>
                </SharingPointsScreen>
              )}
              <SharingPointsScreen path="/sharingpoints">
                <SharingPointsIndexScreen path="/" className="xx" />
                <ShowSharingPointScreen path="/:sharingPointId"></ShowSharingPointScreen>
              </SharingPointsScreen>
              <SwapDetailView path="/swaps/:swapId" />

              {isAllowedToRead("Location") && (
                <>
                  <LocationsScreen lang={lang} path="/locations"></LocationsScreen>
                  <LocationsCreateScreen lang={lang} path="/locations/create/*" />
                  <LocationsCreateScreen lang={lang} path="/locations/edit/:locationId/*" />
                  <ShowLocationsScreen lang={lang} path="/locations/:locationId" />
                </>
              )}
              {isAllowedToRead("NewBattery") && (
                <BatteriesScreen lang={lang} path="/batteries/*" />
              )}
              {isAllowedToRead("SharingPoint") && (
                <SwapStatisticScreen lang={lang} path="/swaps" />
              )}
              {isAllowedToRead("SharingPoint") && (
                <>
                  <SystemSummaryScreen lang={lang} path="/" />
                  <SystemSummaryScreen lang={lang} path="/system-summary" />
                </>
              )}
              {isAllowedToRead("ErrorHistory") && (
                <MainScreen path="/error-history" session={session} />
              )}
              {isAllowedToRead("ErrorHistory") &&
                !isAllowedToRead("SharingPoint") && (
                <Redirect noThrow from="/" to="/error-history" />
              )}
              {isAllowedToWrite("SharingPoint") && (
                <ReleaseNotes lang={lang} path="/release-notes" />
              )}
              <ProfileScreen path="/users/:username" />
              <UserManagementScreen path="/users" />
              <ProfileScreen path="/profile" />
              <ProfileScreen path="/profile/:userId" />
              <UserGroupDetailScreen path="/user-groups/:userGroupName" />
              <RedirectFromBatteryTracking path="/battery-tracking" />
              <RedirectFromBatteryChargeHistoryPath path="/batteries/charge-history" />
              <SwobbeePublicLocationMap path="/swobbee-locations" />
              <SignupScreenError path="/oauth2-signup" />
              <Redirect noThrow from="*" to="/" />
            </Router>
          </NewLayout>
        )}
      </Location>
      {/* <AckMessenger acks={acks} /> */}
      <RestApiMessenger acks={restApiResponse} />
    </ErrorBoundary>
  );
};
export default AuthenticatedApp;

/** Legacy URL: /battery-tracking → /batteries?tab=chargeHistory (query merged). */
const RedirectFromBatteryTracking = () => {
  const { search } = useLocation();
  const merged = {
    ...qs.parse(search, { ignoreQueryPrefix: true }),
    tab: "chargeHistory",
  };
  const q = qs.stringify(merged);
  return <Redirect to={`/batteries${q ? `?${q}` : ""}`} noThrow />;
};

/** Legacy URL: /batteries/charge-history → /batteries?tab=chargeHistory */
const RedirectFromBatteryChargeHistoryPath = () => {
  const { search } = useLocation();
  const merged = {
    ...qs.parse(search, { ignoreQueryPrefix: true }),
    tab: "chargeHistory",
  };
  const q = qs.stringify(merged);
  return <Redirect to={`/batteries${q ? `?${q}` : ""}`} noThrow />;
};

const SignupScreenError = () => (
  <Grid
    centered
    style={{ padding: 20, margin: "auto" }}
    columns="1"
  >
    <GridColumn textAlign="center">
      <Icon name="warning sign" size="massive" color="yellow" />
    </GridColumn>

    <GridColumn textAlign="center">
      <Header>
        You are currently logged in with another account. Please logout and
        start the Login over.
      </Header>
    </GridColumn>
  </Grid>
);
